import { useEffect } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  DayRange,
  InternalOpsPage,
  InternalOpsState,
  SortableTable,
  StatCard,
  formatCount,
  useDayRange,
  useInternalOpsFetch,
  withQuery,
} from "./internalOpsUi";
import { API_ENDPOINTS } from "../../config/apiConfig";

function placeLabel(point) {
  return [point.city, point.region, point.country].filter(Boolean).join(", ");
}

function heatColor(count, max) {
  const ratio = max > 0 ? count / max : 0;
  const lightness = 62 - Math.round(ratio * 28);
  return `hsl(8, 78%, ${lightness}%)`;
}

function FitPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = points.map((point) => [point.latitude, point.longitude]);
    if (bounds.length === 1) {
      map.setView(bounds[0], 4);
      return;
    }
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 5 });
  }, [map, points]);
  return null;
}

function SignInMap({ points }) {
  const max = points.reduce((highest, point) => Math.max(highest, point.count), 0);
  return (
    <div className="h-[480px] overflow-hidden rounded-xl border border-slate-200 bg-white">
      <MapContainer center={[20, 0]} zoom={2} scrollWheelZoom className="h-full w-full">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitPoints points={points} />
        {points.map((point) => (
          <CircleMarker
            key={`${point.latitude}-${point.longitude}-${placeLabel(point)}`}
            center={[point.latitude, point.longitude]}
            radius={Math.min(28, 6 + Math.sqrt(point.count) * 4)}
            pathOptions={{
              color: heatColor(point.count, max),
              fillColor: heatColor(point.count, max),
              fillOpacity: 0.72,
              weight: 1,
            }}
          >
            <Popup>
              <div className="text-xs">
                <p className="font-semibold">{placeLabel(point)}</p>
                <p>{formatCount(point.count)} sign-ins</p>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

export default function InternalOpsLoginMap() {
  const [days, setDays] = useDayRange();
  const { data, loading, error } = useInternalOpsFetch(
    withQuery(API_ENDPOINTS.internalOpsLoginAreas, { days })
  );
  const points = data?.points || [];

  return (
    <InternalOpsPage
      title="Sign-in map"
      subtitle="Where accounts sign in from. Each dot is a city-sized area from a successful sign-in, not a stored IP address."
    >
      <div className="mb-6 flex justify-end">
        <DayRange days={days} onChange={setDays} />
      </div>
      <InternalOpsState loading={loading} error={error}>
        {data ? (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <StatCard label={`Sign-ins (${data.days}d)`} value={data.total} />
              <StatCard label="Places" value={data.places} />
            </div>
            <SignInMap points={points} />
            <SortableTable
              rows={points}
              empty="No public sign-ins in this window yet."
              rowKey={(row) => `${row.latitude}-${row.longitude}-${placeLabel(row)}`}
              columns={[
                {
                  key: "place",
                  label: "Place",
                  sortValue: (row) => placeLabel(row),
                  render: (row) => placeLabel(row),
                },
                { key: "count", label: "Sign-ins", sortValue: (row) => row.count, render: (row) => formatCount(row.count) },
              ]}
            />
            <p className="text-xs text-slate-500">
              {data.note} Place names are from{" "}
              <a href="https://db-ip.com" className="underline">DB-IP</a>.
              {data.geoip_ready ? "" : " The local location database is not installed yet, so new sign-ins are not being placed."}
            </p>
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}
